import type { WorldId } from '@/config/worlds';
import { ToneSoundManager } from '@/game/toneSound';
import { logger } from '@/services/logging';
import type { ReactNode } from 'react';
import { createContext, useContext, useEffect, useRef } from 'react';

interface SoundContextValue {
  sound: ToneSoundManager | null;
  playButtonClick: () => Promise<void>;
  playButtonHover: () => Promise<void>;
  playMove: () => Promise<void>;
  playSlide: () => Promise<void>;
  playLevelStart: () => Promise<void>;
  playLevelComplete: () => Promise<void>;
  playGameOver: () => Promise<void>;
  playLevelFailed: () => Promise<void>;
  playHitObstacle: () => Promise<void>;
  playWallHit: () => Promise<void>;
  playIceSlide: () => Promise<void>;
  playFireHit: () => Promise<void>;
  playFreeze: () => Promise<void>;
  playWarp: () => Promise<void>;
  playCollectCoin: () => Promise<void>;
  playPowerup: () => Promise<void>;
  playAchievement: () => Promise<void>;
  // Ability-specific sounds
  playShield: () => Promise<void>;
  playInvincibility: () => Promise<void>;
  playTeleport: () => Promise<void>;
  playDoubleCoins: () => Promise<void>;
  playMagnetCoins: () => Promise<void>;
  playExtraLife: () => Promise<void>;
  playSkipLevel: () => Promise<void>;
  playHint: () => Promise<void>;
  playUndo: () => Promise<void>;
  playSlowTime: () => Promise<void>;
  // Enemy sounds
  playEnemyMove: () => Promise<void>;
  playEnemyCollision: () => Promise<void>;
  playHurt: () => Promise<void>;
  playShieldAbsorb: () => Promise<void>;
  // Card sounds
  playCardDraw: () => Promise<void>;
  playCardPlay: () => Promise<void>;
  // Modal sounds
  playModalOpen: () => Promise<void>;
  playModalClose: () => Promise<void>;
  // Navigation sounds
  playLevelTransition: () => Promise<void>;
  // Background music
  startWorldMusic: (worldId: WorldId) => Promise<void>;
  startLobbyMusic: () => Promise<void>;
  stopWorldMusic: () => void;
  setMusicEnabled: (enabled: boolean) => void;
  isMusicEnabled: () => boolean;
  setMusicVolume: (volume: number) => void;
  getMusicVolume: () => number;
  // Sound settings
  setEnabled: (enabled: boolean) => void;
  setVolume: (volume: number) => void;
  isEnabled: () => boolean;
  getVolume: () => number;
}

const SoundContext = createContext<SoundContextValue | undefined>(undefined);

interface SoundProviderProps {
  children: ReactNode;
}

export function SoundProvider({ children }: SoundProviderProps) {
  const soundManagerRef = useRef<ToneSoundManager | null>(null);

  // Create instance eagerly (outside effect) so it's available on first render.
  // This is safe because the constructor only reads localStorage - no side effects.
  if (!soundManagerRef.current) {
    soundManagerRef.current = new ToneSoundManager();
  }

  useEffect(() => {
    // Initialize audio context on first user interaction (browser requirement)
    const handleFirstInteraction = async () => {
      if (soundManagerRef.current) {
        await soundManagerRef.current.initialize();
        logger.info('sound', 'Audio context initialized after user interaction');
      }
      document.removeEventListener('click', handleFirstInteraction);
      document.removeEventListener('keydown', handleFirstInteraction);
      document.removeEventListener('touchstart', handleFirstInteraction);
    };

    document.addEventListener('click', handleFirstInteraction, { passive: true });
    document.addEventListener('keydown', handleFirstInteraction, { passive: true });
    document.addEventListener('touchstart', handleFirstInteraction, { passive: true });

    return () => {
      document.removeEventListener('click', handleFirstInteraction);
      document.removeEventListener('keydown', handleFirstInteraction);
      document.removeEventListener('touchstart', handleFirstInteraction);

      if (soundManagerRef.current) {
        soundManagerRef.current.dispose();
        soundManagerRef.current = null;
      }
    };
  }, []);

  // Delegate all methods through the ref so they always use the current instance.
  // This avoids the stale-closure problem where methods bind to a null snapshot.
  const ref = soundManagerRef;
  const noop = () => Promise.resolve();

  const contextValue: SoundContextValue = {
    sound: ref.current,
    playButtonClick: () => ref.current?.playButtonClick() ?? noop(),
    playButtonHover: () => ref.current?.playButtonHover() ?? noop(),
    playMove: () => ref.current?.playMove() ?? noop(),
    playSlide: () => ref.current?.playSlide() ?? noop(),
    playLevelStart: () => ref.current?.playLevelStart() ?? noop(),
    playLevelComplete: () => ref.current?.playLevelComplete() ?? noop(),
    playGameOver: () => ref.current?.playGameOver() ?? noop(),
    playLevelFailed: () => ref.current?.playLevelFailed() ?? noop(),
    playHitObstacle: () => ref.current?.playHitObstacle() ?? noop(),
    playWallHit: () => ref.current?.playWallHit() ?? noop(),
    playIceSlide: () => ref.current?.playIceSlide() ?? noop(),
    playFireHit: () => ref.current?.playFireHit() ?? noop(),
    playFreeze: () => ref.current?.playFreeze() ?? noop(),
    playWarp: () => ref.current?.playWarp() ?? noop(),
    playCollectCoin: () => ref.current?.playCollectCoin() ?? noop(),
    playPowerup: () => ref.current?.playPowerup() ?? noop(),
    playAchievement: () => ref.current?.playAchievement() ?? noop(),
    // Ability-specific sounds
    playShield: () => ref.current?.playShield() ?? noop(),
    playInvincibility: () => ref.current?.playInvincibility() ?? noop(),
    playTeleport: () => ref.current?.playTeleport() ?? noop(),
    playDoubleCoins: () => ref.current?.playDoubleCoins() ?? noop(),
    playMagnetCoins: () => ref.current?.playMagnetCoins() ?? noop(),
    playExtraLife: () => ref.current?.playExtraLife() ?? noop(),
    playSkipLevel: () => ref.current?.playSkipLevel() ?? noop(),
    playHint: () => ref.current?.playHint() ?? noop(),
    playUndo: () => ref.current?.playUndo() ?? noop(),
    playSlowTime: () => ref.current?.playSlowTime() ?? noop(),
    // Enemy sounds
    playEnemyMove: () => ref.current?.playEnemyMove() ?? noop(),
    playEnemyCollision: () => ref.current?.playEnemyCollision() ?? noop(),
    playHurt: () => ref.current?.playHurt() ?? noop(),
    playShieldAbsorb: () => ref.current?.playShieldAbsorb() ?? noop(),
    // Card sounds
    playCardDraw: () => ref.current?.playCardDraw() ?? noop(),
    playCardPlay: () => ref.current?.playCardPlay() ?? noop(),
    // Modal sounds
    playModalOpen: () => ref.current?.playModalOpen() ?? noop(),
    playModalClose: () => ref.current?.playModalClose() ?? noop(),
    // Navigation sounds
    playLevelTransition: () => ref.current?.playLevelTransition() ?? noop(),
    // Background music
    startWorldMusic: (worldId: WorldId) => ref.current?.startWorldMusic(worldId) ?? noop(),
    startLobbyMusic: () => ref.current?.startLobbyMusic() ?? noop(),
    stopWorldMusic: () => ref.current?.stopWorldMusic(),
    setMusicEnabled: (enabled: boolean) => ref.current?.setMusicEnabled(enabled),
    isMusicEnabled: () => ref.current?.isMusicEnabled() ?? false,
    setMusicVolume: (volume: number) => ref.current?.setMusicVolume(volume),
    getMusicVolume: () => ref.current?.getMusicVolume() ?? 0,
    // Sound settings
    setEnabled: (enabled: boolean) => ref.current?.setEnabled(enabled),
    setVolume: (volume: number) => ref.current?.setVolume(volume),
    isEnabled: () => ref.current?.isEnabled() ?? false,
    getVolume: () => ref.current?.getVolume() ?? 0,
  };

  return <SoundContext.Provider value={contextValue}>{children}</SoundContext.Provider>;
}

export function useSound() {
  const context = useContext(SoundContext);
  if (context === undefined) {
    throw new Error('useSound must be used within a SoundProvider');
  }
  return context;
}
